import { Alert, Box, Snackbar, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import Flex from '@Components/Bases/Flex';
import { FormDatePicker } from '@Components/Form';
import {
  disabledDatePickerTypingStyles,
  disabledFormInputStyles,
} from '@Components/Form/styles';
import ConfirmModal from '@Components/Modal/ConfirmModal';
import { useSaveCommitteeApprovalDate } from '@Domains/ReviewSummaryReport';
import { useDisclosure } from '@hooks/useDisclosure';
import {
  convertBuddhistYearToGregorian,
  formatFullThaiDate,
} from '@Utils/datetime';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { useReviewSummaryReportForm } from './ReviewSummaryReportFormProvider/ReviewSummaryReportFormProvider';
import { useReviewSummaryReport } from './ReviewSummaryReportProvider/ReviewSummaryReportProvider';
import {
  checkValidOperatingYear,
  mapCommitteeApprovalDateToFromValues,
  mapCommitteeApprovalDateToPayload,
} from './utils';

type SelectedCommitteeApprovalDate = {
  prev?: Dayjs;
  current: Dayjs | null;
};

const ApprovalReport = () => {
  const { id = '' } = useParams();
  const [{ error }, saveCommitteeApprovalDate] =
    useSaveCommitteeApprovalDate(id);
  const { triggerRefetch } = useReviewSummaryReport();
  const { progressData, refetchProgressWithUpdate } = useProgressBar();

  const [openedModal, { open: openModal, close: closeModal }] = useDisclosure();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();

  const { watch, setValue, getValues } = useReviewSummaryReportForm();
  const isDraftReport = watch('draftReportStatus');
  const isAllSectionsCompleted = watch('completedAllSection');
  const publishedDate = watch('publishedDate');
  const committeeApprovalDate = watch('committeeApprovalDate');
  const isPublished = Boolean(publishedDate);

  const [selectedDate, setSelectedDate] =
    useState<SelectedCommitteeApprovalDate>({
      prev: committeeApprovalDate,
      current: null,
    });

  const startOperatingYear = convertBuddhistYearToGregorian(
    progressData?.report?.startOperatingYear
  );

  const isValidOperatingYear = checkValidOperatingYear(startOperatingYear);

  const handleChange = (date: Dayjs | null) => {
    setSelectedDate({ prev: selectedDate.prev, current: date });
    openModal();
  };

  const handleClose = () => {
    setSelectedDate({ prev: selectedDate.prev, current: null });
    closeModal();
  };

  const handleConfirm = async () => {
    setValue('committeeApprovalDate', selectedDate.current ?? undefined);
    const formValues = getValues();
    const payload = mapCommitteeApprovalDateToPayload(formValues);

    try {
      const response = await saveCommitteeApprovalDate({
        data: payload,
      });

      if (response) {
        const updateFormValues = mapCommitteeApprovalDateToFromValues(
          response.data
        );
        setValue(
          'committeeApprovalDate',
          updateFormValues.committeeApprovalDate
        );
        setSelectedDate({
          prev: selectedDate.current ? selectedDate.current : undefined,
          current: null,
        });
        triggerRefetch?.();
      }
    } catch (error) {
      console.error(error);
      setSelectedDate({ prev: selectedDate.prev, current: null });
    } finally {
      refetchProgressWithUpdate(false);
      openSnackbar();
      closeModal();
    }
  };

  return (
    <>
      <Box bgcolor={'white'} p={2}>
        <Typography fontWeight={800}>วันที่มีมติจากคณะกรรมการบริษัท</Typography>
        <Typography mt={2}>
          กรุณาระบุวันที่ที่คณะกรรมการบริษัทมีมติเห็นชอบ โดยระบุวันที่ตามรูปแบบ
          วัน/เดือน/ปี
        </Typography>
        <Flex mt={2} justifyContent={'center'}>
          <FormDatePicker
            name="committeeApprovalDate"
            sx={
              !isDraftReport ||
              !isAllSectionsCompleted ||
              isPublished ||
              !isValidOperatingYear
                ? disabledFormInputStyles
                : disabledDatePickerTypingStyles
            }
            slotProps={{
              toolbar: { hidden: false },
              field: { value: selectedDate.prev },
            }}
            localeText={{ toolbarTitle: 'เลือก วัน เดือน ปี' }}
            minDate={dayjs(new Date(startOperatingYear, 0, 1))}
            maxDate={dayjs(new Date(startOperatingYear + 1, 11, 31))}
            onChange={(value) => handleChange(value)}
            value={
              selectedDate.current ? selectedDate.current : selectedDate.prev
            }
            onYearChange={() => closeModal()}
            disableFuture
          />
        </Flex>
      </Box>
      <ConfirmModal
        opened={openedModal}
        close={handleClose}
        topic="ยืนยันวันที่มีมติจากคณะกรรมการบริษัท"
        description={`ท่านต้องการยืนยันวันที่มีมติจากคณะกรรมการบริษัท เป็นวันที่ ${formatFullThaiDate(
          selectedDate.current
        )} ใช่หรือไม่ หากใช่กรุณากดยืนยัน`}
        confirm={handleConfirm}
      />
      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!error ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {!!error ? 'เกิดข้อผิดพลาดในการบันทึก' : 'บันทึกสําเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ApprovalReport;
